





// src/components/profile/ProfileSettings.js
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProfile, changePassword } from '../../features/auth/authSlice';

const ProfileSettings = () => {
    const dispatch = useDispatch();
    const { user, isLoading, isSuccess, isError, message } = useSelector(state => state.auth);

    const [activeTab, setActiveTab] = useState('profile');

    const [profileData, setProfileData] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        company: user?.company || '',
        job_title: user?.job_title || ''
    });

    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_new_password: ''
    });

    const [profileErrors, setProfileErrors] = useState({});
    const [passwordErrors, setPasswordErrors] = useState({});

    const [profileSuccess, setProfileSuccess] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // Handle profile data changes
    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData({
            ...profileData,
            [name]: value
        });

        // Clear error when field is edited
        if (profileErrors[name]) {
            setProfileErrors({
                ...profileErrors,
                [name]: ''
            });
        }
    };

    // Handle password data changes
    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData({
            ...passwordData,
            [name]: value
        });

        // Clear error when field is edited
        if (passwordErrors[name]) {
            setPasswordErrors({
                ...passwordErrors,
                [name]: ''
            });
        }
    };

    // Validate profile form
    const validateProfileForm = () => {
        const errors = {};

        if (!profileData.first_name.trim()) {
            errors.first_name = 'First name is required';
        }

        if (!profileData.last_name.trim()) {
            errors.last_name = 'Last name is required';
        }

        return errors;
    };

    // Validate password form
    const validatePasswordForm = () => {
        const errors = {};

        if (!passwordData.current_password) {
            errors.current_password = 'Current password is required';
        }

        if (!passwordData.new_password) {
            errors.new_password = 'New password is required';
        } else if (passwordData.new_password.length < 8) {
            errors.new_password = 'Password must be at least 8 characters';
        }

        if (passwordData.new_password !== passwordData.confirm_new_password) {
            errors.confirm_new_password = 'Passwords do not match';
        }

        return errors;
    };

    // Handle profile form submit
    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setProfileSuccess('');

        const errors = validateProfileForm();
        if (Object.keys(errors).length > 0) {
            setProfileErrors(errors);
            return;
        }

        try {
            await dispatch(updateProfile(profileData)).unwrap();
            setProfileSuccess('Profile updated successfully');

            // Clear success message after 3 seconds
            setTimeout(() => {
                setProfileSuccess('');
            }, 3000);
        } catch (error) {
            if (error.field) {
                setProfileErrors({
                    ...profileErrors,
                    [error.field]: error.message
                });
            } else {
                setProfileErrors({
                    ...profileErrors,
                    general: error.message || 'Failed to update profile'
                });
            }
        }
    };

    // Handle password form submit
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordSuccess('');

        const errors = validatePasswordForm();
        if (Object.keys(errors).length > 0) {
            setPasswordErrors(errors);
            return;
        }

        try {
            await dispatch(changePassword(passwordData)).unwrap();
            setPasswordSuccess('Password changed successfully');

            // Reset password form
            setPasswordData({
                current_password: '',
                new_password: '',
                confirm_new_password: ''
            });

            // Clear success message after 3 seconds
            setTimeout(() => {
                setPasswordSuccess('');
            }, 3000);
        } catch (error) {
            if (error.field) {
                setPasswordErrors({
                    ...passwordErrors,
                    [error.field]: error.message
                });
            } else {
                setPasswordErrors({
                    ...passwordErrors,
                    general: error.message || 'Failed to change password'
                });
            }
        }
    };

    return (
        <div className="profile-settings">
            <h1>Profile Settings</h1>

            <div className="profile-tabs">
                <div
                    className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    Profile Information
                </div>
                <div
                    className={`tab ${activeTab === 'password' ? 'active' : ''}`}
                    onClick={() => setActiveTab('password')}
                >
                    Change Password
                </div>
            </div>

            <div className="tab-content">
                {/* Profile Tab */}
                {activeTab === 'profile' && (
                    <div className="profile-form-container">
                        {profileErrors.general && (
                            <div className="error-message">{profileErrors.general}</div>
                        )}

                        {profileSuccess && (
                            <div className="success-message">{profileSuccess}</div>
                        )}

                        <form onSubmit={handleProfileSubmit} className="profile-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="first_name">First Name</label>
                                    <input
                                        type="text"
                                        id="first_name"
                                        name="first_name"
                                        value={profileData.first_name}
                                        onChange={handleProfileChange}
                                        className={profileErrors.first_name ? 'error' : ''}
                                    />
                                    {profileErrors.first_name && (
                                        <div className="error-text">{profileErrors.first_name}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="last_name">Last Name</label>
                                    <input
                                        type="text"
                                        id="last_name"
                                        name="last_name"
                                        value={profileData.last_name}
                                        onChange={handleProfileChange}
                                        className={profileErrors.last_name ? 'error' : ''}
                                    />
                                    {profileErrors.last_name && (
                                        <div className="error-text">{profileErrors.last_name}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="company">Company (Optional)</label>
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        value={profileData.company}
                                        onChange={handleProfileChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="job_title">Job Title (Optional)</label>
                                    <input
                                        type="text"
                                        id="job_title"
                                        name="job_title"
                                        value={profileData.job_title}
                                        onChange={handleProfileChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={user?.email || ''}
                                    disabled
                                    className="disabled"
                                />
                                <small>Email address cannot be changed</small>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Password Tab */}
                {activeTab === 'password' && (
                    <div className="password-form-container">
                        {passwordErrors.general && (
                            <div className="error-message">{passwordErrors.general}</div>
                        )}

                        {passwordSuccess && (
                            <div className="success-message">{passwordSuccess}</div>
                        )}

                        <form onSubmit={handlePasswordSubmit} className="password-form">
                            <div className="form-group">
                                <label htmlFor="current_password">Current Password</label>
                                <input
                                    type="password"
                                    id="current_password"
                                    name="current_password"
                                    value={passwordData.current_password}
                                    onChange={handlePasswordChange}
                                    className={passwordErrors.current_password ? 'error' : ''}
                                />
                                {passwordErrors.current_password && (
                                    <div className="error-text">{passwordErrors.current_password}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="new_password">New Password</label>
                                <input
                                    type="password"
                                    id="new_password"
                                    name="new_password"
                                    value={passwordData.new_password}
                                    onChange={handlePasswordChange}
                                    className={passwordErrors.new_password ? 'error' : ''}
                                />
                                {passwordErrors.new_password && (
                                    <div className="error-text">{passwordErrors.new_password}</div>
                                )}
                                <div className="password-requirements">
                                    Password must be at least 8 characters
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirm_new_password">Confirm New Password</label>
                                <input
                                    type="password"
                                    id="confirm_new_password"
                                    name="confirm_new_password"
                                    value={passwordData.confirm_new_password}
                                    onChange={handlePasswordChange}
                                    className={passwordErrors.confirm_new_password ? 'error' : ''}
                                />
                                {passwordErrors.confirm_new_password && (
                                    <div className="error-text">{passwordErrors.confirm_new_password}</div>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Updating...' : 'Change Password'}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileSettings;